/**
 * Peer Service
 * Unified peer interactions: discovery, CRUD, config, messaging
 */

import { mergePeers, createPeerFromToolAgent } from './peer.js'
import { createChatEngine } from './chat-engine.js'

export function createPeerService({
  botConfigService, discoverRoles, roleCall, deleteRole, excludeBotIds,
  // Chat engine deps — required for handleUserMessage
  chatFn, buildPrompt,
  // Tool agent — stateless LLM peer with tools support, auto-injected into all chat sessions
  toolAgent,
}) {
  const toolAgentPeer = toolAgent ? createPeerFromToolAgent(toolAgent) : null

  let cachedPeers = []
  let cachedBots = []

  const discover = async (opts) => {
    const [bots, roles] = await Promise.all([
      botConfigService.getBots(),
      discoverRoles(opts),
    ])
    cachedBots = bots
    cachedPeers = mergePeers(bots, roles, excludeBotIds)
    if (toolAgentPeer) {
      toolAgentPeer.refreshDescription()
      cachedPeers.push(toolAgentPeer)
    }
    return { peers: cachedPeers, bots, roles }
  }

  const getPeer = (roleId) => cachedPeers.find(p => p.roleId === roleId) || null

  const getBot = (botId) => cachedBots.find(b => b.botId === botId) || null

  const addBot = (config) => botConfigService.createBot(config)

  const updateBot = (botId, updates) => botConfigService.updateBot(botId, updates)

  const removePeer = async (roleId) => {
    const peer = getPeer(roleId)
    if (peer?.type === 'llm-bot') {
      await botConfigService.deleteBot(roleId)
    } else {
      await deleteRole(roleId)
    }
  }

  const getWorkerConfig = (roleId) => roleCall(roleId, 'getConfig', [])

  const updateWorkerConfig = (roleId, values) => roleCall(roleId, 'updateConfig', [values])

  const chatEngine = chatFn
    ? createChatEngine({ chatFn, buildPrompt, roleCall })
    : null

  return {
    discover, getPeer, getBot, addBot, updateBot, removePeer, getWorkerConfig, updateWorkerConfig, chatEngine,
    toolAgentPeer,
  }
}
